import { Button, TextInput, Spacing, FormField, Text, Font } from 'zens-storybook'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import { object, string } from 'yup'
import { ApiError, useStore } from 'zens-store'
import { useNumberParams, useParams } from 'zens-router'
import { AuthPanel } from '@dms/component'
import { ValidationError } from '@dms/helper'

const styles = StyleSheet.create({
  input: {
    marginTop: Spacing.M,
    marginBottom: Spacing.XS,
    minHeight: 92,
  },
  button: {
    minWidth: 100,
  },
  text: {
    textAlign: 'center',
    maxWidth: 408,
    marginTop: Spacing.S,
  },
  highlight: {
    fontFamily: Font.medium,
  },
})

const AuthForgot = () => {
  const [{ companyId }] = useNumberParams()
  const [{ email: emailParam }, setParams] = useParams()
  const [email, setEmail] = useState(emailParam || '')
  const [sent, setSent] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()
  const { auth, message } = useStore()

  const schema = object({
    email: string().required(t('Email address is required')).email(t('Invalid email address')),
  })

  useEffect(() => {
    if (emailParam) setEmail(emailParam)
  }, [emailParam])

  useEffect(() => {
    setErrors({})
  }, [email])

  const forgotPassword = async () => {
    try {
      schema.validateSync({ email })
    } catch (error) {
      const { message, path } = error as ValidationError
      setErrors({ [path]: message })
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      setErrors({})
      await auth.forgotPassword(email)
      message.add({
        message: t('A recovery email with resetlink has been sent'),
        status: 'success',
      })
      setParams({ email })
      setSent(true)
      setLoading(false)
    } catch (error) {
      const { fields, message: erroMessage } = error as ApiError
      console.debug(`AUTH: Password reset: ${erroMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Password reset failed')}. ${erroMessage}`,
          status: 'error',
        })
      }
      setLoading(false)
    }
  }

  return (
    <AuthPanel
      backHref={{ pathname: `/signin`, params: { email, companyId } }}
      title={sent ? t('Password resetlink sent') : t('Forgot password?')}
      bottom={
        sent ? undefined : (
          <Button
            title={t('Reset password')}
            style={styles.button}
            onPress={forgotPassword}
            testID="AuthForgot.Submit"
            disabled={loading}
          />
        )
      }
    >
      {sent ? (
        <View testID="AuthForgot.Confirmation">
          <Text style={styles.text}>
            <Trans>
              If an account exists, an email with the link to reset your password has been sent to{' '}
              {/* @ts-expect-error Typing of vars in trans not working correctly  */}
              <Text style={styles.highlight}>{{ email }}</Text>
            </Trans>
          </Text>
          <Text style={styles.text}>
            <Trans>
              Click the link contained in the e-mail. You will be forwarded to a page where you can reset your password.
              The link in the e-mail will expire in 2 hours from now.
            </Trans>
          </Text>
        </View>
      ) : (
        <FormField label={t('Email')} error={errors.email} style={styles.input}>
          <TextInput
            placeholder={t('Enter your email here')}
            value={email}
            onChangeText={setEmail}
            onSubmitEditing={forgotPassword}
            inputMode="email"
            autoCapitalize="none"
            autoComplete="email"
            error={!!errors.email}
            autoFocus
            testID="AuthForgot.Email"
          />
        </FormField>
      )}
    </AuthPanel>
  )
}

export default AuthForgot
